import React, { useState, useEffect, useCallback } from "react";
import {
  Spin,
  notification,
  Modal,
  Button,
  Input,
  Radio,
  Popconfirm,
  Icon,
} from "antd";
import TablePagination, {
  paginationInitConfig,
} from "../../../../components/TablePagination";
import DropdownActions from "../../../../components/DropdownActions";
import api from "../../../../services/api";
import { useFreire } from "../../../../utils/freireContext";
import { formatters } from "../../../../utils/formatters";
import moment from "moment";
import * as texts from "../../locales";
import "./index.css";
import { PixIcon } from "../../../../assets/icons";
import useAccess from "../../../../contexts/PermissionContext/useAccess";
import {
  extractIdentifier,
  getApiErrorMessage,
  getRecordWalletId,
  handleDownloadRefundReceipt,
} from "../../../../utils/download-refund-voucher";

const PaymentHistory = ({ user_id, wallet_id, onWalletUpdate }) => {
  const { freire } = useFreire();
  const [loading, setLoading] = useState(true);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [pagination, setPagination] = useState({
    ...paginationInitConfig,
    limit: 25,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    totalDocs: 0,
    limit: 10,
    totalPages: 1,
    page: 1,
    pagingCounter: 1,
    hasPrevPage: false,
    hasNextPage: false,
    prevPage: null,
    nextPage: null,
  });
  const [refundModal, setRefundModal] = useState({
    visible: false,
    record: null,
  });
  const [refundType, setRefundType] = useState("full");
  const [refundValue, setRefundValue] = useState("");
  const [refundLoading, setRefundLoading] = useState(false);
  const [popConfirmVisible, setPopConfirmVisible] = useState(false);
  const { hasPermission } = useAccess();
  const canRefundWallet = hasPermission?.("refund-wallet-balance") ?? false;
  const canRefundAnyPayment = hasPermission?.("refund-any-payment") ?? false;
  const canRefundTransactions = wallet_id
    ? canRefundWallet
    : canRefundAnyPayment;

  const getRecordChargerHistoryId = (record) =>
    extractIdentifier(
      record?.chargerHistoryId,
      record?.chargerHistoryID,
      record?.charger_history_id,
      record?.chargerHistory,
      record?.chargeHistoryId,
      record?.chargeHistoryID,
      record?.charge_history_id,
      record?.chargeHistory
    );

  const getRecordStationId = (record) =>
    extractIdentifier(
      record?.stationId,
      record?.stationID,
      record?.station_id,
      record?.station?.stationID,
      record?.station?.stationId,
      record?.station?.station_id,
      record?.station?.id
    );

  const isRefundedTransaction = (record) => {
    if (!record) {
      return false;
    }

    const status =
      typeof record.status === "string" ? record.status.toLowerCase() : "";

    if (status === "refunded" || status === "refunding") {
      return true;
    }

    if (record.hasRefund === true) {
      return true;
    }

    return typeof record.refundedValue === "number";
  };

  const shouldShowRefundReceiptButton = (record) => {
    const recordWalletId = wallet_id || getRecordWalletId(record);
    if (!recordWalletId) {
      return false;
    }

    const refundResourceId = extractIdentifier(record?._id);
    if (!refundResourceId) {
      return false;
    }

    const isGuaranteeDebit =
      typeof record?.type === "string" &&
      record.type.toLowerCase() === "guarantee-debit";

    if (record.hasRefund) {
      return true;
    }

    if (isGuaranteeDebit) {
      const transactionId = extractIdentifier(
        record?.transactionID,
        record?.transationID
      );
      return Boolean(transactionId);
    }

    return false;
  };

  const fetchPaymentHistory = useCallback(async () => {
    try {
      setLoading(true);
      // When wallet_id is present, fetch wallet-scoped transactions.
      // Otherwise, fetch all user transactions.
      const endpoint = wallet_id
        ? `/admin/users/${user_id}/wallets/${wallet_id}`
        : `/admin/users/${user_id}/transactions`;

      const { data } = await api.get(endpoint, {
        params: pagination,
      });
      setPaymentHistory(data.transactions);
      setPaginationMeta({
        totalDocs: data.totalDocs,
        limit: data.limit,
        totalPages: data.totalPages,
        page: data.page,
        pagingCounter: data.pagingCounter,
        hasPrevPage: data.hasPrevPage,
        hasNextPage: data.hasNextPage,
        prevPage: data.prevPage,
        nextPage: data.nextPage,
      });
    } catch (error) {
      notification.error({
        message: freire(texts.OOPS),
        description: freire(texts.OOPS),
      });
    } finally {
      setLoading(false);
    }
  }, [user_id, wallet_id, pagination, freire]);

  useEffect(() => {
    if (!user_id) return;
    // If wallet_id is provided, scope to the wallet. If not, fetch user-level.
    if ((wallet_id && user_id) || (!wallet_id && user_id)) {
      fetchPaymentHistory();
    }
  }, [user_id, wallet_id, fetchPaymentHistory]);

  const getColumns = () => {
    const base = [
      {
        title: freire(texts.CREATED_AT),
        dataIndex: "createdAt",
        key: "createdAt",
        className: "col-createdAt",
        render: (date) => <span>{moment(date).format("L LTS")}</span>,
      },
      {
        title: freire(texts.STATION_ID),
        dataIndex: "stationID",
        key: "stationID",
        className: "col-stationId",
        render: (_, record) => {
          if (!getRecordChargerHistoryId(record)) {
            return <span>-</span>;
          }

          const stationId = getRecordStationId(record);
          return <span>{stationId || "-"}</span>;
        },
      },
      {
        title: freire(texts.TYPE),
        dataIndex: "type",
        key: "type",
        className: "col-type",
        render: (type, record) => (
          <span>
            {type}
            {record.payment_type === "pix" ? (
              <Icon component={PixIcon} style={{ marginLeft: 6 }} />
            ) : record.payment_type === "credit_card" ? (
              <Icon type="credit-card" style={{ marginLeft: 6 }} />
            ) : null}
          </span>
        ),
      },
      {
        title: freire(texts.TRANSACTION_VALUE),
        dataIndex: "value",
        key: "value",
        className: "col-value",
        render: (value, record) => {
          const currencyFormatter = formatters.currency(
            freire.userLanguage,
            record?.currency || "BRL"
          );
          const normalizedValue =
            typeof value === "number"
              ? value
              : typeof record?.value === "number"
              ? record.value
              : 0;
          const showOriginal =
            isRefundedTransaction(record) &&
            typeof record?.originalPaidValue === "number";
          const formattedOriginal = showOriginal
            ? currencyFormatter(record.originalPaidValue / 100)
            : null;
          const formattedValue = currencyFormatter(normalizedValue / 100);

          return (
            <span>
              {formattedOriginal ? (
                <span className="payment-history__original-value">
                  {formattedOriginal}
                </span>
              ) : null}
              <span>{formattedValue}</span>
            </span>
          );
        },
      },
      {
        title: freire(texts.REFUNDED_VALUE),
        dataIndex: "refundedValue",
        key: "refundedValue",
        className: "col-refundedValue",
        render: (refundedValue, record) => {
          if (
            !isRefundedTransaction(record) ||
            typeof refundedValue !== "number"
          ) {
            return <span>-</span>;
          }

          const currencyFormatter = formatters.currency(
            freire.userLanguage,
            record?.currency || "BRL"
          );

          return <span>{currencyFormatter(refundedValue / 100)}</span>;
        },
      },
      {
        title: freire(texts.STATUS),
        dataIndex: "status",
        key: "status",
        className: "col-status",
      },
      {
        title: freire(texts.BALANCE_BEFORE),
        dataIndex: "balanceBefore",
        key: "balanceBefore",
        className: "col-balanceBefore",
        render: (value, record) => (
          <span>
            {formatters.currency(
              freire.userLanguage,
              record?.currency || "BRL"
            )(value / 100)}
          </span>
        ),
      },
      {
        title: freire(texts.BALANCE_AFTER),
        dataIndex: "balanceAfter",
        key: "balanceAfter",
        className: "col-balanceAfter",
        render: (_, record) => {
          const balanceAfter = calculateBalanceAfter(record);
          return (
            <span>
              {formatters.currency(
                freire.userLanguage,
                record?.currency || "BRL"
              )(balanceAfter / 100)}
            </span>
          );
        },
      },
    ];

    base.push({
      title: freire(texts.ACTIONS),
      key: "actions",
      className: "col-actions",
      render: (_, record) => <DropdownActions actions={rowActions(record)} />,
    });

    return base;
  };

  const calculateBalanceAfter = (record) => {
    if (record.balanceAfter !== undefined) {
      return record.balanceAfter;
    }
    const { balanceBefore, value, type } = record;
    if (type === "wallet-credit") {
      return balanceBefore + value;
    } else if (type === "wallet-debit") {
      return balanceBefore - value;
    }
    return balanceBefore;
  };

  const openRefundModal = (record) => {
    setRefundModal({ visible: true, record });
    setRefundType("full");
    setRefundValue("");
  };

  const closeRefundModal = () => {
    setRefundModal({ visible: false });
    setRefundType("full");
    setRefundValue("");
    setPopConfirmVisible(false);
  };

  const handleRefund = async () => {
    setRefundLoading(true);
    try {
      const value =
        refundType === "partial"
          ? Math.round(parseFloat(refundValue.replace(/,/g, ".")) * 100)
          : null;
      const record = refundModal.record;
      const recordWalletId = wallet_id || getRecordWalletId(record);
      if (!recordWalletId) {
        throw new Error("Missing wallet/payment method ID for refund");
      }
      const refundResourceId = extractIdentifier(record?._id);
      if (!refundResourceId) {
        throw new Error("Missing transaction ID for refund");
      }
      const { data } = await api.post(
        `/admin/users/${user_id}/wallets/${recordWalletId}/transactions/${refundResourceId}/refund`,
        { refund_value: value }
      );
      if (data && data.wallet && onWalletUpdate) {
        const prevWallet = paymentHistory.find(
          (w) => w._id === data.wallet._id
        );
        if (
          !prevWallet ||
          JSON.stringify(prevWallet) !== JSON.stringify(data.wallet)
        ) {
          onWalletUpdate(data.wallet);
        }
      }
      notification.success({
        message: freire(texts.ALL_RIGHT),
        description: freire(texts.REFUND_DB_WEBHOOK_WARNING),
      });
      closeRefundModal();
      fetchPaymentHistory();
    } catch (error) {
      const fallbackMessage = freire(texts.ERROR_REQUEST_REFUND);
      const endpointMessage = getApiErrorMessage(error);

      notification.error({
        message: freire(texts.OOPS),
        description: (
          <div>
            <div>{fallbackMessage}</div>
            {endpointMessage && endpointMessage !== fallbackMessage ? (
              <div style={{ marginTop: 4 }}>{endpointMessage}</div>
            ) : null}
          </div>
        ),
      });
    } finally {
      setRefundLoading(false);
    }
  };

  const handleCheckPayment = async (record) => {
    try {
      await api.post("/payment-methods/pagarme/webhook", {
        data: { id: record.transationID },
      });
      notification.success({
        message: freire(texts.ALL_RIGHT),
        description: freire(texts.CHECK_PAYMENT_SUCCESS),
      });
    } catch (error) {
      notification.error({
        message: freire(texts.OOPS),
        description: freire(texts.CHECK_PAYMENT_ERROR),
      });
    }
  };

  const rowActions = (record) => {
    const actions = [];
    const recordWalletId = wallet_id || getRecordWalletId(record);
    if (
      canRefundTransactions &&
      record.status === "paid" &&
      record.transationID &&
      (record.gateway_source || record.payment_type === "pix") &&
      recordWalletId
    ) {
      actions.push({
        title: freire(texts.REFUND),
        icon: "undo",
        onClick: () => openRefundModal(record),
      });
    }

    if (
      record.transationID &&
      (["pagarme", "mercado-pago"].includes(record.gateway_source) ||
        record.payment_type === "pix")
    ) {
      actions.push({
        title: freire(texts.CHECK_PAYMENT),
        icon: "search",
        onClick: () => handleCheckPayment(record),
      });
    }

    if (shouldShowRefundReceiptButton(record)) {
      actions.push({
        title: freire(texts.DOWNLOAD_REFUND_RECEIPT),
        icon: "download",
        onClick: () =>
          handleDownloadRefundReceipt({ record, wallet_id, user_id, freire }),
      });
    }
    return actions;
  };

  return (
    <div className="container">
      {/* Refund Modal */}
      <Modal
        title={freire(texts.REFUND)}
        visible={refundModal.visible}
        onCancel={closeRefundModal}
        footer={null}
      >
        <div style={{ marginBottom: 16 }}>
          <Radio.Group
            value={refundType}
            onChange={(e) => setRefundType(e.target.value)}
            style={{ marginBottom: 12 }}
          >
            <Radio value="full">{freire(texts.FULL_REFUND)}</Radio>
            <Radio value="partial">{freire(texts.PARTIAL_REFUND)}</Radio>
          </Radio.Group>
        </div>
        {refundType === "partial" && (
          <Input
            style={{ width: 180, marginBottom: 12 }}
            value={refundValue}
            onChange={(e) => setRefundValue(e.target.value)}
            addonAfter={refundModal.record?.currency || "BRL"}
            placeholder={freire(texts.REFUND_VALUE)}
            type="number"
            min={0}
            max={refundModal.record?.value / 100}
          />
        )}
        <div style={{ marginBottom: 12, color: "#faad14" }}>
          <Icon type="info-circle" /> {freire(texts.REFUND_DB_WEBHOOK_WARNING)}
        </div>
        <Popconfirm
          visible={popConfirmVisible}
          onVisibleChange={setPopConfirmVisible}
          title={
            <div>
              <div>
                {refundType === "full" ? (
                  freire(texts.REFUND_FULL_CONFIRM)
                ) : (
                  <>
                    {freire(texts.REFUND_CONFIRM)}
                    <br />
                    <strong>{freire(texts.OLD_VALUE)}:</strong>{" "}
                    {formatters.currency(
                      freire.userLanguage,
                      refundModal.record?.currency || "BRL"
                    )(refundModal.record?.value / 100)}
                    <br />
                    <strong>{freire(texts.REFUND_VALUE)}:</strong>{" "}
                    {formatters.currency(
                      freire.userLanguage,
                      refundModal.record?.currency || "BRL"
                    )(parseFloat(refundValue.replace(/,/g, ".")))}
                    <br />
                    <strong>{freire(texts.WILL_REMAIN)}:</strong>{" "}
                    {formatters.currency(
                      freire.userLanguage,
                      refundModal.record?.currency || "BRL"
                    )(
                      refundModal.record?.value / 100 -
                        parseFloat(refundValue.replace(/,/g, "."))
                    )}
                  </>
                )}
              </div>
            </div>
          }
          onConfirm={handleRefund}
          okText={freire(texts.YES)}
          cancelText={freire(texts.NO)}
          disabled={
            refundType === "partial" &&
            (!refundValue ||
              isNaN(Number(refundValue.replace(/,/g, "."))) ||
              parseFloat(refundValue.replace(/,/g, ".")) <= 0 ||
              parseFloat(refundValue.replace(/,/g, ".")) >
                refundModal.record?.value / 100)
          }
        >
          <Button
            type="primary"
            loading={refundLoading}
            disabled={
              refundType === "partial" &&
              (!refundValue ||
                isNaN(Number(refundValue.replace(/,/g, "."))) ||
                parseFloat(refundValue.replace(/,/g, ".")) <= 0 ||
                parseFloat(refundValue.replace(/,/g, ".")) >
                  refundModal.record?.value / 100)
            }
            onClick={() => setPopConfirmVisible(true)}
            style={{ width: 120 }}
          >
            <Icon type="save" /> {freire(texts.REFUND)}
          </Button>
        </Popconfirm>
      </Modal>
      {loading && paymentHistory.length === 0 ? (
        <div
          style={{ display: "flex", justifyContent: "center", padding: "50px" }}
        >
          <Spin size="large" tip="Loading payment history..." />
        </div>
      ) : (
        <TablePagination
          style={{
            backgroundColor: "white",
            paddingBottom: 10,
            marginBottom: 20,
          }}
          loading={loading}
          rowKey={(data) => data._id || data.transactionID}
          dataSource={paymentHistory}
          columns={getColumns()}
          total={paginationMeta.totalDocs}
          onPagination={(p) => setPagination((prev) => ({ ...prev, ...p }))}
          current={paginationMeta.page}
          pageSize={paginationMeta.limit}
          rowClassName={(record) => {
            const classes = [];
            if (record.status) {
              classes.push(`row-status-${record.status}`);
            }
            if (record.type) {
              classes.push(`row-type-${record.type}`);
            }
            if (
              shouldShowRefundReceiptButton(record) &&
              !classes.includes("row-status-refunded")
            ) {
              classes.push("row-status-refunded");
            }
            return classes.join(" ");
          }}
        />
      )}
    </div>
  );
};

export default PaymentHistory;
